import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    DollarSign, Users, Plus, Eye, FileDown
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CuentasPorPagar = () => {
    const [resumen, setResumen] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagoModalOpen, setPagoModalOpen] = useState(false);
    const [detalleModalOpen, setDetalleModalOpen] = useState(false);
    const [selectedTrabajador, setSelectedTrabajador] = useState(null);
    const [detalleCuenta, setDetalleCuenta] = useState(null);

    const [pagoForm, setPagoForm] = useState({
        trabajador_id: '',
        trabajador_tipo: '',
        fecha: new Date().toISOString().split('T')[0],
        monto: 0,
        forma_pago: 'efectivo',
        observaciones: ''
    });

    useEffect(() => {
        fetchResumen();
    }, []);

    const fetchResumen = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/trabajadores/cuentas/resumen`);
            setResumen(response.data);
        } catch (error) {
            console.error('Error fetching resumen:', error);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const verDetalle = async (trabajador) => {
        try {
            const response = await axios.get(`${API_URL}/api/trabajadores/${trabajador.id}/cuenta`);
            setDetalleCuenta(response.data);
            setSelectedTrabajador(trabajador);
            setDetalleModalOpen(true);
        } catch (error) {
            console.error('Error fetching detalle:', error);
            toast.error('Error al cargar detalle');
        }
    };

    const openPagoModal = (trabajador) => {
        setSelectedTrabajador(trabajador);
        setPagoForm({
            trabajador_id: trabajador.id,
            trabajador_tipo: trabajador.tipo,
            fecha: new Date().toISOString().split('T')[0],
            monto: 0,
            forma_pago: 'efectivo',
            observaciones: ''
        });
        setPagoModalOpen(true);
    };

    const handlePagoSubmit = async (e) => {
        e.preventDefault();
        if (!pagoForm.monto || pagoForm.monto <= 0) {
            toast.error('Ingrese un monto válido');
            return;
        }

        try {
            await axios.post(`${API_URL}/api/trabajadores/${pagoForm.trabajador_id}/pago`, pagoForm);
            toast.success('Pago registrado exitosamente');
            setPagoModalOpen(false);
            fetchResumen();
        } catch (error) {
            console.error('Error saving pago:', error);
            toast.error(error.response?.data?.detail || 'Error al registrar pago');
        }
    };

    const downloadPdf = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/reportes/trabajadores/pdf`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = 'cuentas_trabajadores.pdf';
            link.click();
            URL.revokeObjectURL(link.href);
            toast.success('PDF descargado');
        } catch (error) {
            toast.error('Error al descargar PDF');
        }
    };

    const getTipoLabel = (tipo) => {
        const tipos = { cortador: 'Cortador', maquilador: 'Maquilador', lavanderia: 'Lavandería' };
        return tipos[tipo] || tipo;
    };

    const getTipoClass = (tipo) => {
        switch (tipo) {
            case 'cortador': return 'badge-info';
            case 'maquilador': return 'badge-warning';
            case 'lavanderia': return 'badge-success';
            default: return 'badge-info';
        }
    };

    const totalDeuda = resumen.reduce((sum, t) => sum + t.saldo_pendiente, 0);
    const totalGenerado = resumen.reduce((sum, t) => sum + t.total_generado, 0);
    const totalPagado = resumen.reduce((sum, t) => sum + t.total_pagado, 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="cuentas-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100" >
                        Cuentas por Pagar - Trabajadores
                    </h1>
                    <p className="text-zinc-400 mt-1">Control de pagos a cortadores, maquiladores y lavandería</p>
                </div>
                <Button onClick={downloadPdf} variant="outline" data-testid="download-cuentas-pdf">
                    <FileDown className="w-4 h-4 mr-2" />
                    Exportar PDF
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-400/10 flex items-center justify-center">
                            <DollarSign className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-zinc-100">${totalGenerado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                            <p className="text-sm text-zinc-400">Total Generado</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-400/10 flex items-center justify-center">
                            <DollarSign className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-zinc-100">${totalPagado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                            <p className="text-sm text-zinc-400">Total Pagado</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-red-400/10 flex items-center justify-center">
                            <DollarSign className="w-6 h-6 text-red-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-red-400">${totalDeuda.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                            <p className="text-sm text-zinc-400">Saldo Pendiente</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Table */}
            <Card className="card">
                <div className="p-4 border-b border-zinc-700">
                    <h3 className="font-semibold text-zinc-100 flex items-center gap-2">
                        <Users className="w-5 h-5 text-amber-400" />
                        Resumen por Trabajador
                    </h3>
                </div>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-zinc-800">
                                    <TableHead className="table-header">Nombre</TableHead>
                                    <TableHead className="table-header">Tipo</TableHead>
                                    <TableHead className="table-header">$/Pieza</TableHead>
                                    <TableHead className="table-header">Total Generado</TableHead>
                                    <TableHead className="table-header">Total Pagado</TableHead>
                                    <TableHead className="table-header">Saldo Pendiente</TableHead>
                                    <TableHead className="table-header text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {resumen.length > 0 ? resumen.map((t) => (
                                    <TableRow key={t.id} className="table-row" data-testid={`trabajador-row-${t.id}`}>
                                        <TableCell className="table-cell font-medium">{t.nombre}</TableCell>
                                        <TableCell className="table-cell">
                                            <span className={getTipoClass(t.tipo)}>{getTipoLabel(t.tipo)}</span>
                                        </TableCell>
                                        <TableCell className="table-cell">${t.precio_por_pieza?.toFixed(2)}</TableCell>
                                        <TableCell className="table-cell">${t.total_generado?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</TableCell>
                                        <TableCell className="table-cell text-emerald-400">${t.total_pagado?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</TableCell>
                                        <TableCell className="table-cell">
                                            <span className={t.saldo_pendiente > 0 ? 'text-red-400 font-semibold' : 'text-emerald-400'}>
                                                ${t.saldo_pendiente?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                            </span>
                                        </TableCell>
                                        <TableCell className="table-cell text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button variant="ghost" size="sm" onClick={() => verDetalle(t)} data-testid={`ver-detalle-${t.id}`}>
                                                    <Eye className="w-4 h-4 text-amber-400" />
                                                </Button>
                                                {t.saldo_pendiente > 0 && (
                                                    <Button size="sm" onClick={() => openPagoModal(t)} className="bg-emerald-500 hover:bg-emerald-600" data-testid={`pagar-${t.id}`}>
                                                        <DollarSign className="w-4 h-4 mr-1" />
                                                        Pagar
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-8 text-zinc-400">
                                            No hay trabajadores con cuentas registradas. Registre empleados como cortador, maquilador o lavandería.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Pago Modal */}
            <Dialog open={pagoModalOpen} onOpenChange={setPagoModalOpen}>
                <DialogContent className="sm:max-w-[450px]" data-testid="pago-modal">
                    <DialogHeader>
                        <DialogTitle >
                            <DollarSign className="w-5 h-5 inline mr-2 text-emerald-400" />
                            Registrar Pago
                        </DialogTitle>
                    </DialogHeader>
                    {selectedTrabajador && (
                        <div className="bg-zinc-800 p-3 rounded-lg mb-4">
                            <p className="font-medium text-zinc-100">{selectedTrabajador.nombre}</p>
                            <p className="text-sm text-zinc-400">
                                Saldo pendiente: <span className="text-red-400 font-semibold">${selectedTrabajador.saldo_pendiente?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                            </p>
                        </div>
                    )}
                    <form onSubmit={handlePagoSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Fecha *</Label>
                                <Input
                                    type="date"
                                    value={pagoForm.fecha}
                                    onChange={(e) => setPagoForm({ ...pagoForm, fecha: e.target.value })}
                                    data-testid="pago-fecha"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Monto *</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    value={pagoForm.monto}
                                    onChange={(e) => setPagoForm({ ...pagoForm, monto: parseFloat(e.target.value) || 0 })}
                                    data-testid="pago-monto"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Forma de Pago</Label>
                            <Select value={pagoForm.forma_pago} onValueChange={(v) => setPagoForm({ ...pagoForm, forma_pago: v })}>
                                <SelectTrigger data-testid="pago-forma">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="efectivo">Efectivo</SelectItem>
                                    <SelectItem value="transferencia">Transferencia</SelectItem>
                                    <SelectItem value="cheque">Cheque</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Observaciones</Label>
                            <Textarea
                                value={pagoForm.observaciones}
                                onChange={(e) => setPagoForm({ ...pagoForm, observaciones: e.target.value })}
                                placeholder="Notas del pago"
                                rows={2}
                                data-testid="pago-observaciones"
                            />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setPagoModalOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-pago-btn">
                                Registrar Pago
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Detalle Modal */}
            <Dialog open={detalleModalOpen} onOpenChange={setDetalleModalOpen}>
                <DialogContent className="sm:max-w-[650px] max-h-[80vh] overflow-y-auto" data-testid="detalle-modal">
                    <DialogHeader>
                        <DialogTitle >
                            Detalle de Cuenta: {selectedTrabajador?.nombre}
                        </DialogTitle>
                    </DialogHeader>
                    {detalleCuenta && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-3 gap-4">
                                <div className="bg-amber-400/5 p-3 rounded-lg text-center">
                                    <p className="text-sm text-zinc-400">Generado</p>
                                    <p className="text-lg font-bold text-amber-400">${detalleCuenta.total_generado?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                                </div>
                                <div className="bg-emerald-400/5 p-3 rounded-lg text-center">
                                    <p className="text-sm text-zinc-400">Pagado</p>
                                    <p className="text-lg font-bold text-emerald-400">${detalleCuenta.total_pagado?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                                </div>
                                <div className="bg-red-400/5 p-3 rounded-lg text-center">
                                    <p className="text-sm text-zinc-400">Pendiente</p>
                                    <p className="text-lg font-bold text-red-400">${detalleCuenta.saldo_pendiente?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                                </div>
                            </div>

                            {detalleCuenta.trabajos?.length > 0 && (
                                <div>
                                    <h4 className="font-semibold text-zinc-100 mb-2">Trabajos Realizados</h4>
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="bg-zinc-800">
                                                    <TableHead className="table-header">Fecha</TableHead>
                                                    <TableHead className="table-header">Piezas</TableHead>
                                                    <TableHead className="table-header">$/Pieza</TableHead>
                                                    <TableHead className="table-header">Total</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {detalleCuenta.trabajos.map((t, i) => (
                                                    <TableRow key={i} className="table-row">
                                                        <TableCell className="table-cell">{t.fecha}</TableCell>
                                                        <TableCell className="table-cell">{t.cantidad_piezas}</TableCell>
                                                        <TableCell className="table-cell">${t.precio_por_pieza?.toFixed(2)}</TableCell>
                                                        <TableCell className="table-cell font-medium">${t.monto_total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            )}

                            {detalleCuenta.pagos?.length > 0 && (
                                <div>
                                    <h4 className="font-semibold text-zinc-100 mb-2">Pagos Realizados</h4>
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="bg-zinc-800">
                                                    <TableHead className="table-header">Fecha</TableHead>
                                                    <TableHead className="table-header">Monto</TableHead>
                                                    <TableHead className="table-header">Forma de Pago</TableHead>
                                                    <TableHead className="table-header">Observaciones</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {detalleCuenta.pagos.map((p, i) => (
                                                    <TableRow key={i} className="table-row">
                                                        <TableCell className="table-cell">{p.fecha}</TableCell>
                                                        <TableCell className="table-cell text-emerald-400 font-medium">${p.monto?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</TableCell>
                                                        <TableCell className="table-cell capitalize">{p.forma_pago}</TableCell>
                                                        <TableCell className="table-cell text-zinc-400">{p.observaciones || '-'}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};
